import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { asFailure, type Failure } from '../api/request'
import {
  abortLearningRun,
  answerLearningCard,
  createCard,
  deferLearningCard,
  deleteCard,
  deleteCardFilter,
  fetchCardFilters,
  fetchAutomaticLearningOptions,
  fetchCards,
  fetchLearningRanks,
  fetchLearningRun,
  fetchLearningSummary,
  fetchTags,
  noClue,
  type AutomaticLearningOptions,
  type CardFilter,
  type Flashcard,
  type LearningParameters,
  type LearningRun,
  type LearningMode,
  type LearningReason,
  type LearningStatus,
  type LearningSummaryCard,
  type LearningSummaryOutcome,
  type ToughCard,
  revealLearningCard,
  saveCardFilter,
  similarCards,
  startAutomaticLearning,
  startContinueLearning,
  startGapLearning,
  startLearningRun,
  resetAutomaticLearningPreferences,
  type TagMode,
  updateCard,
} from '../api/flashcards'
import { useLoad, type Loaded } from '../api/useLoad'
import { AREA_IDS, ENTRY_AREA_ID } from '../frame/areas'
import { Lanes } from '../frame/Lanes'
import { hrefFor, navigate, useRoute } from '../frame/route'
import { useAnnounce } from '../ui/Announce'
import { MARKDOWN } from '../ui/markup'
import { CardForm } from './cards/CardForm'
import { Button } from '../ui/Control'
import { Offer } from '../ui/Offer'
import { Preview } from '../ui/preview/Preview'
import { CardSummary } from './cards/CardSummary'
import { Failed, Waiting } from '../ui/Status'
import { needsWork } from './cards/CardStatus'
import { RoteLearning } from './cards/RoteLearning'
import { RoteSession } from './cards/RoteSession'

const EMPTY_FILTER: CardFilter = { query: '', tags: [], tagMode: 'all' }
const DEFAULT_PARAMETERS: LearningParameters = {
  roundSize: 30,
  minimumReviewPercentage: 70,
  drillRepetitions: 10,
}
/**
 * Asking and learning by heart are two different jobs, so they are two tabs.
 *
 * A round is addressed under *ask*; `#/cards/learn/<id>` still opens one, because links to rounds
 * were handed out before the split.
 */
type CardTab = 'create' | 'ask' | 'learn' | 'cards'

export function Cards() {
  const route = useRoute(AREA_IDS, ENTRY_AREA_ID)
  const area = route.path[0]
  const runId = area === 'ask' || area === 'learn' ? (route.path[1] ?? null) : null
  if (runId !== null) return <Learning runId={runId} />
  /*
    One card has an address of its own: `#/cards/card/<id>`.

    Everything that points at a card from somewhere else — a session, a subject, a search result —
    needs one, and without it they all arrived at `#/cards/<id>`, which names no tab and therefore
    read as the writing surface: somebody who asked for the card they wrote in Tuesday's lecture got
    an empty form.

    It shows that one card **in the collection**, drawn as a card, rather than opening it in the
    editor. What was asked for is *this card*, and a card's shape is what says which way round it is
    asked; changing it is the control already on it, one press away and the same one every other card
    carries.
  */
  const openId = area === 'card' ? (route.path[1] ?? null) : null
  const tab: CardTab =
    area === 'ask'
      ? 'ask'
      : area === 'learn'
        ? 'learn'
        : area === 'cards' || area === 'card'
          ? 'cards'
          : 'create'
  return <CardCollection tab={tab} openId={openId} />
}

function CardCollection({
  tab,
  openId,
}: {
  readonly tab: CardTab
  /** One card asked for by address, which is then the only one shown. `null` for an ordinary tab. */
  readonly openId: string | null
}) {
  const list = useLoad(useCallback((signal: AbortSignal) => fetchCards(signal), []))
  const tagList = useLoad(useCallback((signal: AbortSignal) => fetchTags(signal), []))
  const saved = useLoad(useCallback((signal: AbortSignal) => fetchCardFilters(signal), []))
  const insights = useLoad(
    useCallback((signal: AbortSignal) => fetchLearningRanks(EMPTY_FILTER, undefined, signal), []),
  )
  const announce = useAnnounce()
  const [filter, setFilter] = useState<CardFilter>(EMPTY_FILTER)
  const [selectedFilterId, setSelectedFilterId] = useState<string | undefined>()
  const [filterName, setFilterName] = useState('')
  const [editing, setEditing] = useState<Flashcard | null>(null)
  const [front, setFront] = useState('')
  const [back, setBack] = useState('')
  const [askedFor, setAskedFor] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [reversible, setReversible] = useState(false)
  const [frontFormat, setFrontFormat] = useState(MARKDOWN)
  const [backFormat, setBackFormat] = useState(MARKDOWN)
  const [infinite, setInfinite] = useState(false)
  /** The cards a running acquisition session works through; `null` while none runs. */
  const [session, setSession] = useState<readonly ToughCard[] | null>(null)
  const [edited, setEdited] = useState<Partial<LearningParameters>>({})
  const [onlyTough, setOnlyTough] = useState(false)
  const [tagText, setTagText] = useState('')
  const [similar, setSimilar] = useState<Flashcard[]>([])
  const [failure, setFailure] = useState<Failure | null>(null)
  const [editorKey, setEditorKey] = useState(0)
  const [busy, setBusy] = useState(false)

  const options = useLoad(
    useCallback(
      (signal: AbortSignal) => fetchAutomaticLearningOptions(filter, selectedFilterId, signal),
      [filter, selectedFilterId],
    ),
  )
  const cards = useMemo(() => (list.load.status === 'ready' ? list.load.value : []), [list.load])
  const knownTags = tagList.load.status === 'ready' ? tagList.load.value.map((tag) => tag.name) : []
  const visible = useMemo(() => cards.filter((card) => matches(card, filter)), [cards, filter])
  const duplicateCandidates = front.trim() === '' && back.trim() === '' ? [] : similar
  /**
   * The learning settings, derived rather than copied into state: what is stored for this person in
   * this space, with whatever is being edited on top. One place for round size, review share and drill
   * repetitions, and no effect that could hold a stale value.
   */
  const stored = options.load.status === 'ready' ? options.load.value.preference : null
  const parameters: LearningParameters = {
    roundSize: edited.roundSize ?? stored?.roundSize ?? DEFAULT_PARAMETERS.roundSize,
    minimumReviewPercentage:
      edited.minimumReviewPercentage ??
      stored?.minimumReviewPercentage ??
      DEFAULT_PARAMETERS.minimumReviewPercentage,
    drillRepetitions:
      edited.drillRepetitions ?? stored?.drillRepetitions ?? DEFAULT_PARAMETERS.drillRepetitions,
  }
  /** Every variant's live rank and tough reasons, so the collection can say why a card qualified. */
  const ranks = useMemo(
    () => (insights.load.status === 'ready' ? insights.load.value : []),
    [insights.load],
  )
  const ranksOf = (card: Flashcard) => ranks.filter((rank) => rank.cardId === card.id)
  const isTough = (card: Flashcard) => needsWork(ranksOf(card))
  /*
    The collection is the same selection as everywhere else, with one narrowing of its own.

    It had a search box and nothing else once — no tags, no all-or-any, no saved selections — which
    was exactly backwards: the list of *all* the cards is where somebody goes looking for one, and it
    was the one place that could not be narrowed by the thing every card is filed by. So it reads
    `filter`, the same one asking and learning read, and a selection made here is the selection those
    two start from. What stays its own is "only rote learning": it is a statement about how the cards
    stand rather than about which of them are meant, and it belongs where the cards are looked at.
  */
  const collection = visible.filter((card) => !onlyTough || isTough(card))
  /*
    One card asked for by address: it, and nothing else, whatever the selection says.

    Derived and never copied into state — the address *is* which card is meant, so there is nothing to
    keep in step with it. Empty once the deck is here and this card is not in it, which is a card
    deleted since or an address somebody typed, and is said in those words rather than drawn as an
    empty collection.
  */
  const oneCard = openId === null ? null : (cards.find((card) => card.id === openId) ?? null)
  const shown = openId === null ? collection : oneCard === null ? [] : [oneCard]

  useEffect(() => {
    if (front.trim() === '' && back.trim() === '') {
      return
    }
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      void similarCards(front, back, editing?.id, controller.signal)
        .then(setSimilar)
        .catch((error: unknown) => {
          if (!controller.signal.aborted) setFailure(asFailure(error))
        })
    }, 350)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [front, back, editing?.id])

  const attempt = async (work: () => Promise<void>) => {
    try {
      setBusy(true)
      setFailure(null)
      await work()
    } catch (error) {
      setFailure(asFailure(error))
    } finally {
      setBusy(false)
    }
  }

  const resetDraft = (keepClassification: boolean) => {
    setEditing(null)
    setFront('')
    setBack('')
    if (!keepClassification) {
      setAskedFor('')
      setTags([])
      setReversible(false)
      // The markup stays where it was even between series: somebody writing a run of cards in
      // AsciiDoc is not asking to be put back into Markdown after every save.
    }
    setTagText('')
    setSimilar([])
    setEditorKey((one) => one + 1)
  }

  const store = () => {
    if (busy || front.trim() === '' || back.trim() === '') return
    void attempt(async () => {
      const draftTags = withTag(tags, tagText)
      const draft = { front, back, askedFor, reversible, tags: draftTags, frontFormat, backFormat }
      if (editing === null) {
        await createCard(draft)
        announce('Card added. Write the next one.')
        setTags(draftTags)
        resetDraft(true)
      } else {
        await updateCard(editing.id, draft)
        announce('Card changed.')
        resetDraft(false)
      }
      list.retry()
      tagList.retry()
    })
  }

  const beginEditing = (card: Flashcard) => {
    setEditing(card)
    setFront(card.front)
    setBack(card.back)
    setAskedFor(card.askedFor)
    setTags(card.tags.map((tag) => tag.name))
    setReversible(card.reversible)
    setFrontFormat(card.frontFormat)
    setBackFormat(card.backFormat)
    setEditorKey((one) => one + 1)
    navigate('cards', ['create'])
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const changeFilter = (next: CardFilter) => {
    setSelectedFilterId(undefined)
    setFilter(next)
  }

  if (session !== null) {
    return (
      <Lanes
        full
        overview={{
          label: 'Learning by heart',
          content: (
            <RoteSession
              nuts={session}
              filter={filter}
              filterId={selectedFilterId}
              parameters={parameters}
              onLeave={() => {
                setSession(null)
              }}
              onChanged={() => {
                list.retry()
                insights.retry()
              }}
            />
          ),
        }}
      />
    )
  }

  return (
    <Lanes
      overview={{
        label: 'Cards',
        content: (
          <div className="cards">
            <header className="cards__head">
              <div>
                <h2>Cards</h2>
                <p>Create cards, choose a learning round or maintain your collection.</p>
              </div>
            </header>

            <nav className="cards-tabs" aria-label="Cards">
              <CardTabLink tab="create" current={tab}>
                Create
              </CardTabLink>
              <CardTabLink tab="ask" current={tab}>
                Ask
              </CardTabLink>
              <CardTabLink tab="learn" current={tab}>
                Learn
              </CardTabLink>
              <CardTabLink tab="cards" current={tab}>
                Cards
              </CardTabLink>
            </nav>

            {tab === 'create' && (
              <section
                className="card-write"
                aria-label={editing === null ? 'New flashcard' : 'Edit flashcard'}
                onKeyDown={(event) => {
                  if (
                    event.key === 'Enter' &&
                    (event.metaKey || event.ctrlKey) &&
                    !(event.target instanceof HTMLElement && event.target.closest('.cm-editor'))
                  ) {
                    event.preventDefault()
                    store()
                  }
                }}
              >
                <header className="card-section-head">
                  <div>
                    <p className="card-section-head__eyebrow">
                      {editing === null ? 'New flashcard' : 'Editing flashcard'}
                    </p>
                    <h3>{editing === null ? 'Capture what matters' : 'Refine both sides'}</h3>
                  </div>
                  <p>⌘/Ctrl+Enter saves and opens a fresh card.</p>
                </header>
                <CardForm
                  draft={{ front, back, frontFormat, backFormat, askedFor, tags, reversible }}
                  onDraft={(next) => {
                    setFront(next.front)
                    setBack(next.back)
                    setFrontFormat(next.frontFormat)
                    setBackFormat(next.backFormat)
                    setAskedFor(next.askedFor)
                    setTags([...next.tags])
                    setReversible(next.reversible)
                  }}
                  knownTags={knownTags}
                  tagText={tagText}
                  onTagText={setTagText}
                  busy={busy}
                  editorKey={editorKey}
                  autoFocus
                  submitLabel={editing === null ? 'Add card' : 'Save changes'}
                  onSubmit={store}
                  onCancel={
                    editing === null
                      ? undefined
                      : () => {
                          resetDraft(false)
                        }
                  }
                >
                  {duplicateCandidates.length > 0 && (
                    <aside className="duplicates" aria-label="Similar cards">
                      <strong>Similar cards already exist</strong>
                      <p>You can still save this card. Compare both sides first.</p>
                      {duplicateCandidates.map((card) => (
                        <CardSummary
                          key={card.id}
                          card={card}
                          onEdit={() => {
                            beginEditing(card)
                          }}
                        />
                      ))}
                    </aside>
                  )}
                </CardForm>
              </section>
            )}

            {failure !== null && <Failed failure={failure} />}

            {/*
              One selection, above all three tabs that work on cards.

              Which cards are meant is the same question whether they are about to be asked, learnt by
              heart or simply looked through, so it is asked once and answered once. Choosing a set in
              the collection and then pressing *Ask* starts a round of exactly those cards, without
              anybody having to say it twice.
            */}
            {tab !== 'create' && openId === null && (
              <section
                className="card-selection"
                aria-label={
                  tab === 'ask'
                    ? 'Choose cards to ask'
                    : tab === 'learn'
                      ? 'Choose cards to learn by heart'
                      : 'Choose cards to look through'
                }
              >
                <header className="card-section-head card-section-head--learn">
                  <div>
                    <p className="card-section-head__eyebrow">
                      {tab === 'cards' ? 'Your collection' : 'Study set'}
                    </p>
                    <h3>
                      {tab === 'ask'
                        ? 'Choose what helps now'
                        : tab === 'learn'
                          ? 'Choose what to work on'
                          : 'Find what you are after'}
                    </h3>
                    <p>
                      {visible.length} of {cards.length} cards match this selection.
                    </p>
                  </div>
                </header>

                <div className="card-filters">
                  <label className="card-field">
                    <span className="card-field__label">Search the cards</span>
                    <input
                      className="card-field__control"
                      value={filter.query}
                      onChange={(event) => {
                        changeFilter({ ...filter, query: event.target.value })
                      }}
                      placeholder="Words in the cue or either side"
                    />
                  </label>

                  {knownTags.length > 0 && (
                    <fieldset className="filter-tags">
                      <legend className="card-field__label">Narrow by tag</legend>
                      <div className="filter-tags__choices">
                        {knownTags.map((tag) => (
                          <label key={tag} className="filter-chip">
                            <input
                              className="choice-input"
                              type="checkbox"
                              checked={filter.tags.includes(tag)}
                              onChange={(event) => {
                                changeFilter({
                                  ...filter,
                                  tags: event.target.checked
                                    ? [...filter.tags, tag]
                                    : filter.tags.filter((one) => one !== tag),
                                })
                              }}
                            />
                            <span>{tag}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  )}

                  {filter.tags.length > 0 && (
                    <TagModeSwitch
                      value={filter.tagMode}
                      onChange={(tagMode) => {
                        changeFilter({ ...filter, tagMode })
                      }}
                    />
                  )}

                  {/*
                    How the cards stand, not which of them are meant — so it narrows the list here and
                    is deliberately not part of what a round is started from.
                  */}
                  {tab === 'cards' && (
                    <label className="filter-chip card-filters__standing">
                      <input
                        className="choice-input"
                        type="checkbox"
                        checked={onlyTough}
                        onChange={(event) => {
                          setOnlyTough(event.target.checked)
                        }}
                      />
                      <span>{`Only rote learning (${cards.filter(isTough).length})`}</span>
                    </label>
                  )}
                </div>

                <section className="saved-filters" aria-label="Saved selections">
                  <div className="saved-filters__write">
                    <label className="card-field">
                      <span className="card-field__label">Save this selection</span>
                      <input
                        className="card-field__control"
                        value={filterName}
                        placeholder="e.g. Exam week"
                        onChange={(event) => {
                          setFilterName(event.target.value)
                        }}
                      />
                    </label>
                    <Button
                      disabled={filterName.trim() === ''}
                      onClick={() => {
                        void attempt(async () => {
                          await saveCardFilter(filterName, filter)
                          setFilterName('')
                          saved.retry()
                          announce('Selection saved.')
                        })
                      }}
                    >
                      Save selection
                    </Button>
                  </div>
                  {saved.load.status === 'ready' && saved.load.value.length > 0 && (
                    <div className="saved-filters__list">
                      {saved.load.value.map((one) => (
                        <span className="saved-filter" key={one.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setFilter(one.filter)
                              setSelectedFilterId(one.id)
                            }}
                          >
                            {one.name}
                          </button>
                          <button
                            type="button"
                            aria-label={`Delete saved selection: ${one.name}`}
                            onClick={() => {
                              void attempt(async () => {
                                await deleteCardFilter(one.id)
                                saved.retry()
                              })
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </section>

                {tab === 'ask' ? (
                  <IntelligentLearningPanel
                    filter={filter}
                    filterId={selectedFilterId}
                    matchingCards={visible.length}
                    infinite={infinite}
                    onInfiniteChange={setInfinite}
                    options={options}
                    parameters={parameters}
                    onParameters={setEdited}
                    onReset={() => {
                      setEdited({})
                      options.retry()
                    }}
                    onFailure={setFailure}
                  />
                ) : tab === 'learn' ? (
                  <RoteLearning
                    filter={filter}
                    filterId={selectedFilterId}
                    parameters={parameters}
                    onStart={setSession}
                    onFailure={setFailure}
                    onChanged={() => {
                      list.retry()
                      insights.retry()
                    }}
                  />
                ) : null}
              </section>
            )}

            {list.load.status === 'waiting' && <Waiting label="Fetching your cards." />}
            {list.load.status === 'failed' && (
              <Failed
                failure={list.load.failure}
                onRetry={list.retry}
                title="The cards did not load"
              />
            )}
            {list.load.status === 'ready' && cards.length === 0 && (
              <Offer
                title="Your first flashcard"
                lead="Put the prompt on the front and what you want to remember on the back. Plain text is already centred and readable; Markdown is there when it helps."
                points={[
                  'Tags stay selected while you enter a series.',
                  'A reversible card is asked once in each direction.',
                  'Learning works with buttons, touch and a keyboard.',
                ]}
              />
            )}
            {list.load.status === 'ready' &&
              (tab === 'ask' || tab === 'learn') &&
              visible.length > 0 && (
                <section
                  className="card-results"
                  aria-label={tab === 'ask' ? 'Cards in this round' : 'Cards in this selection'}
                >
                  <header className="card-results__head">
                    <div>
                      <p className="card-section-head__eyebrow">
                        {tab === 'ask' ? 'Round preview' : 'Selection'}
                      </p>
                      <h3>{tab === 'ask' ? 'Cards in this round' : 'Cards in this selection'}</h3>
                    </div>
                    <span>{visible.length}</span>
                  </header>
                  <ul className="card-list">
                    {visible.map((card) => (
                      <li key={card.id}>
                        <CardSummary
                          card={card}
                          answerVisible={false}
                          insights={ranksOf(card)}
                          tough={isTough(card)}
                          onEdit={() => {
                            beginEditing(card)
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            {list.load.status === 'ready' && tab === 'cards' && cards.length > 0 && (
              <section
                className="card-results"
                aria-label={openId === null ? 'Your cards' : 'The card you came for'}
              >
                <header className="card-results__head">
                  <div>
                    <p className="card-section-head__eyebrow">
                      {openId === null ? 'Collection' : 'One card'}
                    </p>
                    <h3>{openId === null ? 'Your cards' : 'The card you came for'}</h3>
                  </div>
                  <span>{shown.length}</span>
                </header>

                {/* The way back out of one card, said as a control rather than left to the browser. */}
                {openId !== null && (
                  <p className="cards__one-of-many">
                    <a href={hrefFor('cards', ['cards'])}>Show all your cards</a>
                  </p>
                )}

                {shown.length === 0 ? (
                  <p className="cards__empty">
                    {openId === null
                      ? 'No card matches this.'
                      : 'That card is not there any more. It may have been deleted since you came by it.'}
                  </p>
                ) : (
                  <ul className="card-list">
                    {shown.map((card) => (
                      <li key={card.id}>
                        <CardSummary
                          card={card}
                          insights={ranksOf(card)}
                          tough={isTough(card)}
                          onEdit={() => {
                            beginEditing(card)
                          }}
                          onDelete={() => {
                            void attempt(async () => {
                              await deleteCard(card.id)
                              list.retry()
                              insights.retry()
                              announce('Card deleted.')
                            })
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
            {list.load.status === 'ready' &&
              tab === 'ask' &&
              cards.length > 0 &&
              visible.length === 0 && (
                <p className="cards__empty">No card matches this selection.</p>
              )}
          </div>
        ),
      }}
    />
  )
}

function CardTabLink({
  tab,
  current,
  children,
}: {
  readonly tab: CardTab
  readonly current: CardTab
  readonly children: string
}) {
  return (
    <a
      className="cards-tab"
      href={hrefFor('cards', [tab])}
      aria-current={tab === current ? 'page' : undefined}
    >
      {children}
    </a>
  )
}

function IntelligentLearningPanel({
  filter,
  filterId,
  matchingCards,
  infinite,
  onInfiniteChange,
  options,
  parameters,
  onParameters,
  onReset,
  onFailure,
}: {
  readonly filter: CardFilter
  readonly filterId?: string
  readonly matchingCards: number
  readonly infinite: boolean
  readonly onInfiniteChange: (value: boolean) => void
  /** Loaded once for the whole area, because the learning settings belong to both tabs. */
  readonly options: Loaded<AutomaticLearningOptions>
  readonly parameters: LearningParameters
  readonly onParameters: (parameters: LearningParameters) => void
  readonly onReset: () => void
  readonly onFailure: (failure: Failure | null) => void
}) {
  const [mode, setMode] = useState<LearningMode>('automatic')
  const [busy, setBusy] = useState(false)
  const roundSize = parameters.roundSize
  const reviewPercentage = parameters.minimumReviewPercentage

  const start = async (wantedMode: LearningMode) => {
    if (busy) return
    try {
      setBusy(true)
      onFailure(null)
      const parameters = { roundSize, minimumReviewPercentage: reviewPercentage }
      const run =
        wantedMode === 'automatic'
          ? await startAutomaticLearning(filter, parameters, infinite, filterId)
          : wantedMode === 'gaps'
            ? await startGapLearning(filter, roundSize, infinite, filterId)
            : await startContinueLearning(filter, parameters, infinite, filterId)
      navigate('cards', ['ask', run.id])
    } catch (error) {
      onFailure(asFailure(error))
    } finally {
      setBusy(false)
    }
  }

  if (options.load.status === 'waiting')
    return <Waiting label="Calculating useful learning rounds." />
  if (options.load.status === 'failed') {
    return (
      <Failed
        failure={options.load.failure}
        onRetry={options.retry}
        title="Learning suggestions did not load"
      />
    )
  }

  const ready = options.load.value
  const active = ready.modes.find((one) => one.mode === mode)!
  const expectedReviews = Math.ceil((roundSize * (mode === 'gaps' ? 100 : reviewPercentage)) / 100)
  const expectedNew = Math.max(0, roundSize - expectedReviews)
  const primaryLabel =
    mode === 'automatic' && !active.urgent ? 'Continue learning' : MODE_COPY[mode].action

  return (
    <section className="smart-learning" aria-label="Intelligent learning">
      <header className="smart-learning__progress">
        <div
          className="learning-progress"
          aria-label={`Learning progress ${ready.learningProgress} of 100`}
        >
          <strong>{ready.learningProgress}</strong>
          <span>Learning progress</span>
          <progress value={ready.learningProgress} max={100} />
        </div>
        <div>
          <strong>{ready.reviewedVariants} reviewed</strong>
          <span>{ready.newVariants} new variants in this set</span>
          <small>{nextUsefulCopy(ready.nextUsefulAt)}</small>
        </div>
      </header>

      <div className="learning-modes" role="group" aria-label="Learning goals">
        {ready.modes.map((option) => {
          const copy = MODE_COPY[option.mode]
          return (
            <button
              type="button"
              className={`learning-mode-card learning-mode-card--${option.mode}${mode === option.mode ? ' learning-mode-card--active' : ''}`}
              aria-label={copy.title}
              aria-pressed={mode === option.mode}
              key={option.mode}
              onClick={() => {
                setMode(option.mode)
              }}
            >
              <span>{copy.eyebrow}</span>
              <strong>{copy.title}</strong>
              <p>{copy.description}</p>
              <small>
                {option.candidateCount} {option.candidateCount === 1 ? 'candidate' : 'candidates'}
              </small>
              {option.reasons.slice(0, 2).map((reason) => (
                <em key={reason}>{REASON_COPY[reason]}</em>
              ))}
            </button>
          )
        })}
      </div>

      <label className="card-round-infinite learning-infinite">
        <input
          className="choice-input"
          type="checkbox"
          checked={infinite}
          onChange={(event) => {
            onInfiniteChange(event.target.checked)
          }}
        />
        <span className="switch-track" aria-hidden="true">
          <span />
        </span>
        <span className="switch-copy">
          <strong>Repeat until known</strong>
          <small>Repeat missed cards in this round until every card was known.</small>
        </span>
      </label>

      {mode === 'gaps' && active.candidateCount === 0 ? (
        <div className="smart-learning__notice">
          <strong>No learning gap is evidenced yet.</strong>
          <p>
            Learn automatically or continue with new material first; a wrong or spaced answer will
            make uncertainty visible here.
          </p>
        </div>
      ) : (
        <div className="learning-parameters">
          <label>
            <span>Round size</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={200}
              value={roundSize}
              onChange={(event) => {
                onParameters({ ...parameters, roundSize: Number(event.target.value) })
              }}
            />
          </label>
          <label>
            <span>Minimum reviews</span>
            {mode === 'gaps' ? (
              <strong>100%</strong>
            ) : (
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={reviewPercentage}
                aria-valuetext={`${reviewPercentage} percent`}
                onChange={(event) => {
                  onParameters({
                    ...parameters,
                    minimumReviewPercentage: Number(event.target.value),
                  })
                }}
              />
            )}
          </label>
          <label>
            <span>Drill repetitions</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={100}
              value={parameters.drillRepetitions}
              onChange={(event) => {
                onParameters({ ...parameters, drillRepetitions: Number(event.target.value) })
              }}
            />
          </label>
          <p>
            Up to {expectedReviews} reviews and {expectedNew} new variants; missing candidates are
            filled from the other kind.
          </p>
          <Button
            tone="bare"
            onClick={() => {
              void resetAutomaticLearningPreferences()
                .then(onReset)
                .catch((error: unknown) => {
                  onFailure(asFailure(error))
                })
            }}
          >
            Reset defaults
          </Button>
          <Button
            tone="primary"
            disabled={busy || active.selectedCount === 0 || roundSize < 1 || roundSize > 200}
            onClick={() => {
              void start(mode)
            }}
          >
            {primaryLabel} · {Math.min(active.selectedCount, roundSize)}
          </Button>
        </div>
      )}

      <details className="manual-learning">
        <summary>Build a manual round</summary>
        <div>
          <Button
            disabled={busy || matchingCards === 0}
            onClick={() => {
              setBusy(true)
              void startLearningRun(filter, infinite)
                .then((run) => {
                  navigate('cards', ['ask', run.id])
                })
                .catch((error: unknown) => {
                  onFailure(asFailure(error))
                  setBusy(false)
                })
            }}
          >
            Learn all {matchingCards} matching cards
          </Button>
        </div>
      </details>
    </section>
  )
}

const MODE_COPY: Record<
  LearningMode,
  { eyebrow: string; title: string; description: string; action: string }
> = {
  automatic: {
    eyebrow: 'Recommended',
    title: 'Learn automatically',
    description: 'Balances useful reviews, current uncertainty and new material.',
    action: 'Start recommended round',
  },
  gaps: {
    eyebrow: 'Focused',
    title: 'Close gaps',
    description: 'Works on known variants with the clearest current uncertainty.',
    action: 'Close gaps',
  },
  continue: {
    eyebrow: 'Broad',
    title: 'Continue learning',
    description: 'Refreshes broadly and introduces new variants at your chosen ratio.',
    action: 'Continue learning',
  },
}

const REASON_COPY: Record<LearningReason, string> = {
  new_variant: 'New in this set',
  never_selected_in_set: 'Not selected in this set yet',
  long_not_tested: 'Not tested for a long time',
  useful_review_due: 'Useful review is due',
  recent_failure: 'Recent uncertainty',
  repeated_failure: 'Repeated uncertainty',
  failed_after_long_gap: 'Missed after a longer gap',
  positive_long_gap: 'Strong spaced recall',
  low_progress: 'Learning progress has fallen',
  repeatedly_skipped_in_set: 'Often displaced in this set',
  broaden_set_coverage: 'Broadens set coverage',
  recently_selected_in_set: 'Recently selected here',
}

function nextUsefulCopy(at: string | null): string {
  if (at === null) return 'Start with any new variant.'
  const milliseconds = new Date(at).getTime() - Date.now()
  if (milliseconds <= 0) return 'A useful review is available now.'
  const hours = Math.max(1, Math.round(milliseconds / 3_600_000))
  if (hours < 48) return `Next useful review in about ${hours} hours.`
  return `Next useful review in about ${Math.round(hours / 24)} days.`
}

function runKindCopy(kind: LearningRun['kind']): string {
  if (kind === 'automatic') return 'Automatic learning'
  if (kind === 'gaps') return 'Closing gaps'
  if (kind === 'continue') return 'Continuing broadly'
  return 'Manual round'
}

/** Also used where a card set is chosen by filter for a test's task — see `TaskEditor.tsx`. */
export function TagModeSwitch({
  value,
  onChange,
}: {
  readonly value: TagMode
  readonly onChange: (value: TagMode) => void
}) {
  return (
    <fieldset className="tag-mode">
      <legend className="card-field__label">Match selected tags</legend>
      <div className="tag-mode__choices">
        <label className="segment-choice">
          <input
            className="choice-input"
            type="radio"
            name="tag-mode"
            checked={value === 'all'}
            onChange={() => {
              onChange('all')
            }}
          />
          <span>All tags</span>
        </label>
        <label className="segment-choice">
          <input
            className="choice-input"
            type="radio"
            name="tag-mode"
            checked={value === 'any'}
            onChange={() => {
              onChange('any')
            }}
          />
          <span>Any tag</span>
        </label>
      </div>
    </fieldset>
  )
}

function Learning({ runId }: { readonly runId: string }) {
  const load = useLoad(
    useCallback((signal: AbortSignal) => fetchLearningRun(runId, signal), [runId]),
  )
  const [changedRun, setChangedRun] = useState<LearningRun | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)
  const [busy, setBusy] = useState(false)
  const [ending, setEnding] = useState(false)
  const shownAt = useRef(0)
  const run = changedRun ?? (load.load.status === 'ready' ? load.load.value : null)
  useEffect(() => {
    shownAt.current = Date.now()
  }, [run?.current?.id, run?.current?.state])

  const act = useCallback(
    async (work: (elapsed: number) => Promise<LearningRun>) => {
      if (busy) return
      try {
        setBusy(true)
        setFailure(null)
        const elapsed = shownAt.current === 0 ? 0 : Math.max(0, Date.now() - shownAt.current)
        setChangedRun(await work(elapsed))
      } catch (error) {
        setFailure(asFailure(error))
      } finally {
        setBusy(false)
      }
    },
    [busy],
  )

  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)
        return
      if (run?.status !== 'active' || run.current === null) return
      if (run.current.state === 'question' && (event.key === ' ' || event.key === 'Enter')) {
        event.preventDefault()
        void act((elapsed) => revealLearningCard(runId, elapsed))
      } else if (run.current.state === 'question' && event.key.toLowerCase() === 's') {
        event.preventDefault()
        void act((elapsed) => deferLearningCard(runId, elapsed))
      } else if (run.current.state === 'question' && event.key === '0') {
        event.preventDefault()
        void act(() => noClue(runId))
      } else if (run.current.state === 'answer' && event.key === '1') {
        void act(() => answerLearningCard(runId, false))
      } else if (run.current.state === 'answer' && event.key === '2') {
        void act(() => answerLearningCard(runId, true))
      }
    }
    window.addEventListener('keydown', key)
    return () => {
      window.removeEventListener('keydown', key)
    }
  }, [act, run, runId])

  const content =
    load.load.status === 'failed' ? (
      <Failed failure={load.load.failure} onRetry={load.retry} />
    ) : load.load.status === 'waiting' || run === null ? (
      <Waiting label="Opening the learning round." />
    ) : run.status !== 'active' || run.current === null ? (
      <LearningSummary runId={runId} status={run.status} />
    ) : (
      <div className="learning">
        <header className="learning__head">
          <span className="learning__kind">{runKindCopy(run.kind)}</span>
          <span>
            {run.answered} of {run.total}
          </span>
          <progress value={run.answered} max={run.total} />
          {run.infinite && <span className="learning__mode">Infinite · until all known</span>}
          {ending ? (
            <div className="learning__end">
              <span>Unanswered cards count as not known.</span>
              <Button
                tone="bare"
                onClick={() => {
                  setEnding(false)
                }}
              >
                Keep learning
              </Button>
              <Button
                tone="bare"
                className="card-action--danger"
                onClick={() => {
                  void act(() => abortLearningRun(runId))
                }}
              >
                End now
              </Button>
            </div>
          ) : (
            <Button
              tone="bare"
              onClick={() => {
                setEnding(true)
              }}
            >
              End round
            </Button>
          )}
        </header>
        <main className={`learning-card learning-card--${run.current.state}`}>
          <span className="learning-card__direction">
            {run.current.direction === 'reverse' ? 'Reverse' : 'Front'}
          </span>
          {run.current.askedFor !== '' && (
            <span className="learning-card__asked-for">Asked for: {run.current.askedFor}</span>
          )}
          <section className="learning-card__question">
            <span className="learning-card__side-label">Question</span>
            <Preview
              text={run.current.question}
              format={run.current.questionFormat}
              label="Question"
            />
          </section>
          {run.current.state === 'answer' && (
            <section className="learning-card__answer">
              <span className="learning-card__side-label">Answer</span>
              <Preview text={run.current.answer} format={run.current.answerFormat} label="Answer" />
            </section>
          )}
        </main>
        {failure !== null && <Failed failure={failure} />}
        <footer className="learning-actions">
          {run.current.state === 'question' ? (
            <>
              <Button
                disabled={busy}
                onClick={() => {
                  void act((elapsed) => deferLearningCard(runId, elapsed))
                }}
              >
                Later <kbd>S</kbd>
              </Button>
              {/*
                Not the same as "did not know": that one has looked at the answer and judged itself.
                This one says there was nothing to judge — so the card leaves the round instead of
                coming round again, and goes where it can be learned.
              */}
              <Button
                disabled={busy}
                className="card-action--danger"
                onClick={() => {
                  void act(() => noClue(runId))
                }}
              >
                No clue <kbd>0</kbd>
              </Button>
              <Button
                tone="primary"
                disabled={busy}
                onClick={() => {
                  void act((elapsed) => revealLearningCard(runId, elapsed))
                }}
              >
                Reveal <kbd>Space</kbd>
              </Button>
            </>
          ) : (
            <>
              <Button
                disabled={busy}
                onClick={() => {
                  void act(() => answerLearningCard(runId, false))
                }}
              >
                Did not know <kbd>1</kbd>
              </Button>
              <Button
                tone="primary"
                disabled={busy}
                onClick={() => {
                  void act(() => answerLearningCard(runId, true))
                }}
              >
                Knew it <kbd>2</kbd>
              </Button>
            </>
          )}
        </footer>
      </div>
    )
  return <Lanes full overview={{ label: 'Learning', content }} />
}

const SUMMARY_GROUPS: readonly {
  outcome: LearningSummaryOutcome
  title: string
}[] = [
  { outcome: 'incorrect', title: 'Nicht gewusst' },
  { outcome: 'unanswered', title: 'Nicht beantwortet' },
  { outcome: 'correct', title: 'Gewusst' },
]

function LearningSummary({
  runId,
  status,
}: {
  readonly runId: string
  readonly status: LearningStatus
}) {
  const summary = useLoad(
    useCallback((signal: AbortSignal) => fetchLearningSummary(runId, signal), [runId]),
  )

  if (summary.load.status === 'waiting') return <Waiting label="Zusammenfassung wird geladen." />
  if (summary.load.status === 'failed') {
    return <Failed failure={summary.load.failure} onRetry={summary.retry} />
  }
  const value = summary.load.value

  return (
    <div className="learning-summary">
      <header className="learning-summary__head">
        <div>
          <p className="card-section-head__eyebrow">
            {status === 'aborted' ? 'Runde abgebrochen' : 'Runde abgeschlossen'}
          </p>
          <h2>Zusammenfassung</h2>
        </div>
        <Button
          tone="primary"
          onClick={() => {
            navigate('cards', ['ask'])
          }}
        >
          Zurück zu den Karten
        </Button>
      </header>

      {SUMMARY_GROUPS.map((group) => {
        const cards = value.cards.filter((card) => card.outcome === group.outcome)
        return (
          <section
            className={`learning-summary__group learning-summary__group--${group.outcome}`}
            key={group.outcome}
          >
            <header>
              <h3>{group.title}</h3>
              <span>{cards.length}</span>
            </header>
            {cards.length === 0 ? (
              <p className="learning-summary__empty">Keine Karten</p>
            ) : (
              <ul className="learning-summary__cards">
                {cards.map((card) => (
                  <li key={`${card.cardId}-${card.direction}`}>
                    <SummaryCard card={card} showFailures={group.outcome !== 'correct'} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        )
      })}
    </div>
  )
}

function SummaryCard({
  card,
  showFailures,
}: {
  readonly card: LearningSummaryCard
  readonly showFailures: boolean
}) {
  const failures = `${card.incorrectAttempts} ${card.incorrectAttempts === 1 ? 'Fehlversuch' : 'Fehlversuche'} insgesamt`
  return (
    <article className="learning-summary-card">
      <header>
        <span>{card.direction === 'reverse' ? 'Rückwärts' : 'Vorwärts'}</span>
        {showFailures && <strong>{failures}</strong>}
      </header>
      <div className="learning-summary-card__sides">
        <section>
          <span>Frage</span>
          <Preview text={card.question} format={card.questionFormat} label="Frage" />
        </section>
        <section>
          <span>Antwort</span>
          <Preview text={card.answer} format={card.answerFormat} label="Antwort" />
        </section>
      </div>
    </article>
  )
}

/** Also used where a card-set task takes over cards matching a filter — see `TaskEditor.tsx`. */
export function matches(card: Flashcard, filter: CardFilter): boolean {
  const query = filter.query.trim().toLocaleLowerCase()
  if (
    query !== '' &&
    !`${card.askedFor}\n${card.front}\n${card.back}`.toLocaleLowerCase().includes(query)
  )
    return false
  if (filter.tags.length === 0) return true
  const mine = card.tags.map((tag) => tag.name.toLocaleLowerCase())
  const wanted = filter.tags.map((tag) => tag.toLocaleLowerCase())
  return filter.tagMode === 'all'
    ? wanted.every((tag) => mine.includes(tag))
    : wanted.some((tag) => mine.includes(tag))
}

function withTag(tags: readonly string[], raw: string): string[] {
  const name = raw.trim().replace(/\s+/g, ' ')
  if (
    name === '' ||
    tags.some((tag) => tag.localeCompare(name, undefined, { sensitivity: 'base' }) === 0)
  )
    return [...tags]
  return [...tags, name]
}
